import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  Tabs,
  Tab,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Print as PrintIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  RestoreFromTrash as RestoreIcon,
} from '@mui/icons-material';
import { FactureAvoir, FactureAvoirStatus } from '../../types/factureAvoir';
import { User } from '../../types/auth';
import { Client } from '../../types/client';
import { facturesAvoirAPI, userAPI, clientsAPI, auditLogsAPI } from '../../services/api';
import ColumnFilter from '../../components/ColumnFilter';
import { useColumnFilters } from '../../hooks/useColumnFilters';
import { FilterListOff as FilterListOffIcon } from '@mui/icons-material';
import AlertDialog from '../../components/AlertDialog';
import useAlert from '../../hooks/useAlert';
import { getModuleTableTheme } from '../../theme/tableTheme';
import CustomTablePagination from '../../components/CustomTablePagination';
import PermissionButton from '../../components/PermissionButton';
import { extractErrorMessage } from '../../utils/errorHandler';
import usePdfPreview from '../../hooks/usePdfPreview';
import FactureAvoirPreview from './FactureAvoirPreview';
import FactureAvoirFormDialog from './FactureAvoirFormDialog';
// generateFactureAvoirPDF is dynamically imported in the print handler so the
// @react-pdf/renderer chunk only loads when the user clicks Print.

const COMMERCIAL_COLOR = '#4CAF50';

const AVOIR_FILTER_SCHEMA = {
  date:               { type: 'dateRange',    paramFrom: 'date_from', paramTo: 'date_to' },
  client_id:          { type: 'autocomplete', param: 'client_id', transform: (v: string) => Number(v) },
  reference:          { type: 'text',         param: 'reference' },
  facture_reference:  { type: 'text',         param: 'facture_reference' },
  designation:        { type: 'text',         param: 'designation' },
  montant:            { type: 'numberRange',  paramMin: 'montant_min', paramMax: 'montant_max' },
  user_id:            { type: 'autocomplete', param: 'user_id', transform: (v: string) => Number(v) },
} as const;

const FactureAvoirList: React.FC = () => {
  const [avoirsList, setAvoirsList] = useState<FactureAvoir[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [deletedAvoirsList, setDeletedAvoirsList] = useState<FactureAvoir[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tabValue, setTabValue] = useState(0);
  const [previewAvoir, setPreviewAvoir] = useState<FactureAvoir | null>(null);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [formMinimized, setFormMinimized] = useState(false);
  const [editAvoirId, setEditAvoirId] = useState<number | undefined>(undefined);
  const { alertState, showError, showSuccess, showConfirm, hideAlert } = useAlert();
  const { openPreview, pdfPreviewElement } = usePdfPreview();

  const previewAvoirPdf = (avoir: FactureAvoir) => {
    openPreview({
      getBlob: async () => {
        const { getFactureAvoirPDFBlob } = await import('../../utils/factureAvoirPdfReactPdf');
        return getFactureAvoirPDFBlob(avoir);
      },
      fileName: `${avoir.reference}.pdf`,
    });
    auditLogsAPI.logPdfDownload('FactureAvoir', avoir.id, `Aperçu PDF - ${avoir.reference}`);
  };

  // Column filters
  const { filters, update, reset, hasActive, toQueryParams } = useColumnFilters(
    AVOIR_FILTER_SCHEMA,
    { onChange: () => setPage(0) }
  );

  // Fetch users + clients for filter options
  useEffect(() => {
    (async () => {
      try {
        const [u, c] = await Promise.all([
          userAPI.getUsers(0, 1000),
          clientsAPI.getAllForDropdown(),
        ]);
        setUsers(u);
        setClients(c);
      } catch (err) {
        console.error('Error fetching filter options:', err);
      }
    })();
  }, []);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchAvoirs = useCallback(async () => {
    try {
      setLoading(true);
      const skip = page * rowsPerPage;
      const { client_id, user_id, ...extra } = toQueryParams();
      const response = await facturesAvoirAPI.getPaginated(
        skip,
        rowsPerPage,
        debouncedSearch || undefined,
        undefined,
        (user_id as number | undefined),
        (client_id as number | undefined),
        Object.keys(extra).length > 0 ? extra : undefined
      );
      setAvoirsList(response.items);
      setTotalCount(response.total);
    } catch (err) {
      showError('Erreur lors du chargement des factures avoir');
      console.error('Error fetching avoirs:', err);
      setAvoirsList([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, debouncedSearch, filters]);

  const fetchDeletedAvoirs = async () => {
    try {
      setLoading(true);
      const data = await facturesAvoirAPI.getDeleted();
      setDeletedAvoirsList(data);
    } catch (err) {
      showError('Erreur lors du chargement des factures avoir supprimées');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tabValue === 0) {
      fetchAvoirs();
    }
  }, [fetchAvoirs, tabValue]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(0);
    if (newValue === 1) {
      fetchDeletedAvoirs();
    }
  };

  const handleDelete = async (avoir: FactureAvoir) => {
    try {
      await facturesAvoirAPI.delete(avoir.id);
      showSuccess('Facture avoir supprimée avec succès');
      fetchAvoirs();
    } catch (err: any) {
      showError(extractErrorMessage(err));
    }
  };

  const handleRestore = async (avoir: FactureAvoir) => {
    showConfirm(
      `Êtes-vous sûr de vouloir restaurer la facture avoir "${avoir.reference}" ?`,
      async () => {
        try {
          await facturesAvoirAPI.restore(avoir.id);
          showSuccess('Facture avoir restaurée avec succès');
          fetchDeletedAvoirs();
          fetchAvoirs();
        } catch (err: any) {
          showError(extractErrorMessage(err));
        }
      },
      {
        title: 'Confirmer la restauration',
        confirmText: 'Restaurer',
        cancelText: 'Annuler',
      }
    );
  };

  const handleOpenNewAvoir = () => {
    setEditAvoirId(undefined);
    setFormMinimized(false);
    setFormDialogOpen(true);
  };

  const handleOpenEditAvoir = (avoirId: number) => {
    setEditAvoirId(avoirId);
    setFormMinimized(false);
    setFormDialogOpen(true);
  };

  const handleCloseFormDialog = () => {
    setFormDialogOpen(false);
    setFormMinimized(false);
    setEditAvoirId(undefined);
  };

  const handleFormSuccess = () => {
    fetchAvoirs();
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case FactureAvoirStatus.BROUILLON:
        return <Chip label="Brouillon" color="default" size="small" />;
      case FactureAvoirStatus.VALIDEE:
        return <Chip label="Validée" color="success" size="small" />;
      case FactureAvoirStatus.ANNULEE:
        return <Chip label="Annulée" color="error" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get designations as comma-separated string (for display)
  const getDesignations = (avoir: FactureAvoir): string => {
    if (!avoir.items || avoir.items.length === 0) {
      return '-';
    }
    return avoir.items.map(item => item.designation).join(', ');
  };

  // Get designations for tooltip (one per line)
  const getDesignationsForTooltip = (avoir: FactureAvoir): React.ReactNode => {
    if (!avoir.items || avoir.items.length === 0) {
      return '-';
    }
    return (
      <Box>
        {avoir.items.map((item, index) => (
          <Box key={index}>• {item.designation} (Qté: {item.quantite})</Box>
        ))}
      </Box>
    );
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 'none', py: 3, px: 0 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">Gestion des Factures Avoir</Typography>
        {tabValue === 0 && (
          <PermissionButton
            moduleName="Commercial"
            operation="add"
            miniModuleName="Factures Avoir"
            variant="contained"
            onClick={handleOpenNewAvoir}
            startIcon={<AddIcon />}
            sx={{
              backgroundColor: '#4CAF50',
              '&:hover': { backgroundColor: '#388E3C' },
            }}
          >
            Nouvelle Facture Avoir
          </PermissionButton>
        )}
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label={`Factures Avoir Actives (${totalCount})`} />
          <Tab label="Corbeille" />
        </Tabs>
      </Box>

      {tabValue === 0 && (
        <>
          {/* Filters */}
          <Box display="flex" gap={2} mb={3}>
            <TextField
              placeholder="Rechercher par référence ou client..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ minWidth: 350 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {hasActive() && (
              <Button
                size="small"
                startIcon={<FilterListOffIcon />}
                onClick={reset}
                sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
              >
                Effacer les filtres
              </Button>
            )}
          </Box>

          <TableContainer component={Paper} sx={getModuleTableTheme('commercial')}>
            <Table sx={{ tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 100, whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Date
                      <ColumnFilter type="dateRange" value={filters.date} onChange={(v) => update('date', v)} label="date" color={COMMERCIAL_COLOR} />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: 150 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Client
                      <ColumnFilter
                        type="autocomplete"
                        value={filters.client_id}
                        onChange={(v) => update('client_id', v)}
                        label="Client"
                        color={COMMERCIAL_COLOR}
                        options={clients.map(c => ({ value: c.id.toString(), label: c.name }))}
                      />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: 160, whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Avoir N°
                      <ColumnFilter type="text" value={filters.reference} onChange={(v) => update('reference', v)} label="référence" color={COMMERCIAL_COLOR} />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: 160, whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Facture de Base
                      <ColumnFilter type="text" value={filters.facture_reference} onChange={(v) => update('facture_reference', v)} label="facture" color={COMMERCIAL_COLOR} />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: 'auto' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Libellés
                      <ColumnFilter type="text" value={filters.designation} onChange={(v) => update('designation', v)} label="libellé" color={COMMERCIAL_COLOR} />
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ width: 120, whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      Montant
                      <ColumnFilter type="numberRange" value={filters.montant} onChange={(v) => update('montant', v)} label="montant" color={COMMERCIAL_COLOR} />
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={{ width: 180 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      Chargement...
                    </TableCell>
                  </TableRow>
                ) : avoirsList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      Aucune facture avoir trouvée
                    </TableCell>
                  </TableRow>
                ) : (
                  avoirsList.map((avoir) => {
                    const designations = getDesignations(avoir);

                    return (
                      <TableRow key={avoir.id} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {new Date(avoir.date).toLocaleDateString('fr-FR')}
                        </TableCell>
                        <TableCell>
                          <Tooltip title={avoir.client_name || ''} arrow>
                            <Typography
                              variant="body2"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {avoir.client_name}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 500 }}>
                          {avoir.reference}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {avoir.facture_reference || '-'}
                        </TableCell>
                        <TableCell>
                          <Tooltip title={getDesignationsForTooltip(avoir)} arrow>
                            <Typography
                              variant="body2"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {designations}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 500 }}>
                          {Number(avoir.total_net).toLocaleString('fr-FR', {
                            minimumFractionDigits: 2,
                          })}{' '}
                          DH
                        </TableCell>
                        <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                          <IconButton
                            size="small"
                            onClick={() => previewAvoirPdf(avoir)}
                            title="Imprimer"
                          >
                            <PrintIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => setPreviewAvoir(avoir)}
                            title="Aperçu"
                          >
                            <ViewIcon />
                          </IconButton>
                          <PermissionButton
                            moduleName="Commercial"
                            operation="edit"
                            miniModuleName="Factures Avoir"
                            component="IconButton"
                            onClick={() => handleOpenEditAvoir(avoir.id)}
                          >
                            <EditIcon />
                          </PermissionButton>
                          <PermissionButton
                            moduleName="Commercial"
                            operation="delete"
                            miniModuleName="Factures Avoir"
                            component="IconButton"
                            onClick={() => {
                              showConfirm(
                                `Êtes-vous sûr de vouloir supprimer la facture avoir "${avoir.reference}" ?`,
                                () => handleDelete(avoir),
                                {
                                  title: 'Confirmer la suppression',
                                  confirmText: 'Supprimer',
                                  cancelText: 'Annuler',
                                }
                              );
                            }}
                            color="error"
                          >
                            <DeleteIcon />
                          </PermissionButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <CustomTablePagination
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}

      {tabValue === 1 && (
        <>
          <TableContainer component={Paper} sx={getModuleTableTheme('commercial')}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Avoir N°</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell align="right">Montant</TableCell>
                  <TableCell align="center">Statut</TableCell>
                  <TableCell>Date de Suppression</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      Chargement...
                    </TableCell>
                  </TableRow>
                ) : deletedAvoirsList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      Aucune facture avoir supprimée
                    </TableCell>
                  </TableRow>
                ) : (
                  deletedAvoirsList
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((avoir) => (
                      <TableRow key={avoir.id}>
                        <TableCell>
                          {new Date(avoir.date).toLocaleDateString('fr-FR')}
                        </TableCell>
                        <TableCell>{avoir.reference}</TableCell>
                        <TableCell>{avoir.client_name}</TableCell>
                        <TableCell align="right">
                          {Number(avoir.total_net).toLocaleString('fr-FR', {
                            minimumFractionDigits: 2,
                          })}{' '}
                          DH
                        </TableCell>
                        <TableCell align="center">{getStatusChip(avoir.status)}</TableCell>
                        <TableCell>
                          {avoir.deleted_at
                            ? new Date(avoir.deleted_at).toLocaleDateString('fr-FR')
                            : '-'}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => handleRestore(avoir)}
                            color="primary"
                            title="Restaurer"
                          >
                            <RestoreIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <CustomTablePagination
            count={deletedAvoirsList.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}

      {/* Preview Modal */}
      {previewAvoir && (
        <FactureAvoirPreview
          avoir={previewAvoir}
          open={!!previewAvoir}
          onClose={() => setPreviewAvoir(null)}
          onPrint={() => {
            const a = previewAvoir;
            setPreviewAvoir(null);
            previewAvoirPdf(a);
          }}
          onEdit={() => {
            setPreviewAvoir(null);
            handleOpenEditAvoir(previewAvoir.id);
          }}
        />
      )}

      {/* Form Dialog */}
      <FactureAvoirFormDialog
        open={formDialogOpen}
        onClose={handleCloseFormDialog}
        onSuccess={handleFormSuccess}
        avoirId={editAvoirId}
        minimized={formMinimized}
        onMinimizedChange={setFormMinimized}
      />

      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        showCancel={alertState.showCancel}
      />

      {/* PDF Preview (print/download from the viewer toolbar) */}
      {pdfPreviewElement}
    </Box>
  );
};

export default FactureAvoirList;
